import React, { FC, useState, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useSubscriptionStore, SubscriptionStatus, CardBrand } from 'src/store/subscriptionStore';
import {
  useSubscriptionStateQuery,
  useRetryPastDuePaymentMutation,
  useCancelScheduledIntervalSwitchMutation,
  useBillingProfileQuery,
  usePaymentMethodsQuery,
} from 'src/api/subscription/useSubscriptionApi';
import { ReactComponent as UserChevronLeftSVG } from 'src/assets/images/onboarding-trainer/components/sidebar/user-chevron-left.svg';
import { ReactComponent as ManagePaymentMethodNextSVG } from 'src/assets/images/onboarding-trainer/subscription-icons/billing-history/manage-payment-method-next.svg';
import { ReactComponent as BillingHistoryDocSVG } from 'src/assets/images/onboarding-trainer/subscription-icons/my-subscription/billing-history-doc.svg';
import { ReactComponent as QuestionsContactSupportSVG } from 'src/assets/images/onboarding-trainer/subscription-icons/my-subscription/questions-contact-support.svg';
import { ReactComponent as TrendDownSVG } from 'src/assets/images/onboarding-trainer/subscription-icons/common/trend-down.svg';
import { ReactComponent as CreateWithAiSVG } from 'src/assets/images/onboarding-trainer/subscription-icons/my-subscription/create-with-ai.svg';
import { ReactComponent as PaymentMethodSVG } from 'src/assets/images/onboarding-trainer/subscription-icons/my-subscription/payment-method.svg';
import { ReactComponent as CardNotChargedShieldSVG } from 'src/assets/images/onboarding-trainer/subscription-icons/my-subscription/card-not-charged-shield.svg';
import { ReactComponent as PaymentFailedVectorSVG } from 'src/assets/images/onboarding-trainer/subscription-icons/payment-failed/vector.svg';
import { ReactComponent as CvvHintSVG } from 'src/assets/images/onboarding-trainer/subscription-icons/add-payment-method/cvv-hint.svg';
import { CardBrandLogo, getCardBrandDisplayName } from '../components/CardBrandLogo';
import { BillingInfoModal } from '../modals/BillingInfoModal';
import { PaymentMethodModal } from '../modals/PaymentMethodModal';
import { RemovePaymentMethodModal } from '../modals/RemovePaymentMethodModal';
import { toast } from 'src/utils/appAlert';
import { Spin } from 'antd';
import { LoadingOutlined } from '@ant-design/icons';
import './MySubscription.scss';

export const MySubscription: FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const queryStatus = searchParams.get('status') as SubscriptionStatus | null;

  const { billingCycle, billingInfo, paymentMethods, removePaymentMethod } = useSubscriptionStore();

  const {
    data: serverState,
    isLoading: isStateLoading,
    refetch: refetchState,
  } = useSubscriptionStateQuery();

  const { data: serverBillingProfile, refetch: refetchBillingProfile } = useBillingProfileQuery();
  const { data: serverPaymentMethods, refetch: refetchPaymentMethods } = usePaymentMethodsQuery();
  const retryMutation = useRetryPastDuePaymentMutation();
  const cancelScheduleMutation = useCancelScheduledIntervalSwitchMutation();

  const currentStatus: SubscriptionStatus =
    queryStatus || (serverState?.status as SubscriptionStatus) || 'inactive';

  const formatSubDate = (isoString?: string | null, fallback = '—') => {
    if (!isoString) return fallback;
    try {
      const d = new Date(isoString);
      return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
    } catch {
      return fallback;
    }
  };

  const dynamicNextBillingDate = formatSubDate(serverState?.current_period_end);
  const dynamicAccessEndsDate = formatSubDate(
    serverState?.access_until || serverState?.current_period_end,
  );
  const dynamicGracePeriodDate = formatSubDate(
    serverState?.past_due?.grace_period_ends_at || serverState?.current_period_end,
  );
  const dynamicExpiredDate = formatSubDate(
    serverState?.access_until || serverState?.current_period_end,
    '—',
  );

  const displayAmount = serverState?.amount?.formatted || '—';

  // Trial duration calculation
  const [currentTimestamp] = useState(() => Date.now());
  const trialDaysRemaining = useMemo(() => {
    const trialEndIso = serverState?.trial?.ends_at;
    if (!trialEndIso) return null;
    const ends = new Date(trialEndIso).getTime();
    const diffDays = Math.ceil((ends - currentTimestamp) / (1000 * 60 * 60 * 24));
    return Math.max(0, diffDays);
  }, [serverState?.trial?.ends_at, currentTimestamp]);

  // Modals
  const [isBillingModalOpen, setIsBillingModalOpen] = useState(false);
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);
  const [isRemoveModalOpen, setIsRemoveModalOpen] = useState(false);

  const serverCards = serverPaymentMethods?.payment_methods || [];
  const defaultMethodCard =
    serverCards.find((c: any) => c.id === serverPaymentMethods?.default_payment_method_id) ||
    serverCards.find((c: any) => c.is_default || c.isDefault) ||
    serverCards[0];

  const defaultCard =
    (serverState?.default_payment_method
      ? {
          id: serverState.default_payment_method.id,
          brand: serverState.default_payment_method.brand.toLowerCase() as CardBrand,
          last4: serverState.default_payment_method.last4,
          expiry: `${serverState.default_payment_method.exp_month} / ${serverState.default_payment_method.exp_year}`,
          cardholderName: serverState.default_payment_method.cardholder_name,
          isDefault: true,
        }
      : null) ||
    (defaultMethodCard
      ? {
          id: defaultMethodCard.id,
          brand: (defaultMethodCard.brand?.toLowerCase() || 'visa') as CardBrand,
          last4: defaultMethodCard.last4,
          expiry:
            defaultMethodCard.expiry ||
            (defaultMethodCard.exp_month && defaultMethodCard.exp_year
              ? `${defaultMethodCard.exp_month} / ${defaultMethodCard.exp_year}`
              : '—'),
          cardholderName: defaultMethodCard.cardholder_name || '',
          isDefault: true,
        }
      : null) ||
    paymentMethods.find((c) => c.isDefault) ||
    paymentMethods[0];

  const isBusinessBilling =
    serverBillingProfile?.billingType === 'business' || billingInfo.billingType === 'business';

  const handleBack = () => {
    if (window.history.state && window.history.state.idx > 0) {
      navigate(-1);
    } else {
      navigate('/onboarding-trainer/dashboard');
    }
  };

  const handleGoToBillingHistory = () => {
    navigate('/onboarding-trainer/dashboard/subscription/billing-history');
  };

  const handleGoToManagePayment = () => {
    navigate('/onboarding-trainer/dashboard/subscription/manage-payment-method');
  };

  const handleGoToSupport = () => {
    navigate('/onboarding-trainer/dashboard/subscription/contact-support');
  };

  const handleCreateWithAi = () => {
    navigate('/onboarding-trainer/dashboard/program-generator/ai');
  };

  const handleCancelSubscription = () => {
    navigate('/onboarding-trainer/dashboard/subscription/cancel');
  };

  const handleReactivate = () => {
    navigate('/onboarding-trainer/dashboard/subscription/reactivate');
  };

  const handleUpgradeToPro = () => {
    navigate('/onboarding-trainer/dashboard/subscription');
  };

  const handleRetryPayment = () => {
    retryMutation.mutate(undefined, {
      onSuccess: () => {
        toast.success('Payment processed successfully!');
        refetchState();
      },
      onError: (err: any) => {
        toast.error(err.userMessage || 'Payment retry failed. Please update your card.');
      },
    });
  };

  const handleCancelScheduledSwitch = () => {
    cancelScheduleMutation.mutate(undefined, {
      onSuccess: () => {
        toast.success('Scheduled interval switch cancelled.');
      },
      onError: (err: any) => {
        toast.error(err.userMessage || 'Failed to cancel scheduled switch.');
      },
    });
  };

  const handleConfirmRemoveCard = () => {
    if (defaultCard) {
      removePaymentMethod(defaultCard.id);
      toast.success('Payment method removed.');
    }
  };

  if (isStateLoading) {
    return (
      <div
        className="my-subscription-page-container"
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '60vh',
        }}
      >
        <Spin indicator={<LoadingOutlined style={{ fontSize: 32, color: '#E2F163' }} spin />} />
      </div>
    );
  }

  return (
    <div className="my-subscription-page-container">
      {/* Top Header */}
      <div className="subscription-header-row">
        <div className="header-left">
          <button type="button" className="sub-back-title-btn" onClick={handleBack}>
            <UserChevronLeftSVG />
            <span>{'My Subscription'}</span>
          </button>
          <p className="sub-header-subtitle">
            {'Manage your plan, billing details, and payment method.'}
          </p>
        </div>

        <div className="header-right-actions">
          <button type="button" className="btn-billing-history" onClick={handleGoToBillingHistory}>
            <BillingHistoryDocSVG />
            <span>{'Billing History'}</span>
            <ManagePaymentMethodNextSVG />
          </button>
        </div>
      </div>

      {/* Main 2-Column Grid */}
      <div className="my-sub-grid">
        {/* Left Column */}
        <div className="my-sub-left-col">
          {/* Main Status Plan Card */}
          <div className="sub-main-card">
            {/* Scheduled Interval Switch Banner */}
            {serverState?.pending_update && (
              <div className="sub-pending-switch-banner">
                <div className="pending-text">
                  <strong>{'Scheduled Switch: '}</strong>
                  <span>
                    {serverState.pending_update.message ||
                      `Your subscription is scheduled to switch to ${
                        serverState.pending_update.pending_interval === 'month'
                          ? 'Monthly'
                          : 'Yearly'
                      } billing on ${formatSubDate(serverState.pending_update.pending_change_at)}.`}
                  </span>
                </div>
                <button
                  type="button"
                  className="btn-cancel-switch"
                  onClick={handleCancelScheduledSwitch}
                  disabled={cancelScheduleMutation.isPending}
                >
                  {cancelScheduleMutation.isPending ? 'Cancelling...' : 'Cancel Switch'}
                </button>
              </div>
            )}

            {/* Status 1: ACTIVE / TRIALING */}
            {(currentStatus === 'active' || currentStatus === 'trialing') && (
              <>
                <div className="card-top-row">
                  <div className="title-with-badge">
                    <h2>{'Pro Plan'}</h2>
                    {currentStatus === 'trialing' ? (
                      <span className="status-pill trial-pill">{'14-Day Free Trial'}</span>
                    ) : (
                      <span className="status-pill active-pill">{'Active'}</span>
                    )}
                  </div>
                </div>
                <p className="status-card-description">
                  {'Ai and motion tracking are unlocked for your account.'}
                </p>

                <div className="sub-details-list">
                  <div className="sub-detail-item">
                    <span className="detail-name">{'Billing Cycle'}</span>
                    <span className="detail-value capitalize">
                      {serverState?.billing_interval || billingCycle}
                    </span>
                  </div>

                  {currentStatus === 'trialing' && (
                    <>
                      <div className="sub-detail-item">
                        <span className="detail-name">{'Trial Period Ends'}</span>
                        <span className="detail-value">
                          {formatSubDate(serverState?.trial?.ends_at)}
                        </span>
                      </div>
                      {trialDaysRemaining !== null && (
                        <div className="sub-detail-item">
                          <span className="detail-name">{'Days Remaining'}</span>
                          <span className="detail-value">{`${trialDaysRemaining} day${
                            trialDaysRemaining === 1 ? '' : 's'
                          }`}</span>
                        </div>
                      )}
                    </>
                  )}

                  <div className="sub-detail-item">
                    <span className="detail-name">{'Next Billing Date'}</span>
                    <span className="detail-value">{dynamicNextBillingDate}</span>
                  </div>
                  <div className="sub-detail-item">
                    <span className="detail-name">{'Amount'}</span>
                    <span className="detail-value">{displayAmount}</span>
                  </div>
                  <div className="sub-detail-item">
                    <span className="detail-name">{'Payment Method'}</span>
                    <span className="detail-value card-val">
                      {defaultCard ? (
                        <>
                          <CardBrandLogo
                            brand={defaultCard.brand}
                            style={{ width: 32, height: 18 }}
                          />
                          <span>{`${getCardBrandDisplayName(defaultCard.brand)} •••• ${defaultCard.last4}`}</span>
                        </>
                      ) : (
                        <span>{'No payment method'}</span>
                      )}
                    </span>
                  </div>
                </div>

                {/* Trial Expiration Warning Notice */}
                {currentStatus === 'trialing' &&
                  trialDaysRemaining !== null &&
                  trialDaysRemaining <= 3 && (
                    <div
                      className="sub-warning-banner past-due-warning"
                      style={{ marginTop: 18, marginBottom: 18 }}
                    >
                      <CardNotChargedShieldSVG />
                      <span>{`Your free trial will conclude in ${trialDaysRemaining} day${
                        trialDaysRemaining === 1 ? '' : 's'
                      }. Your default card will be charged on ${formatSubDate(
                        serverState?.trial?.ends_at,
                      )}.`}</span>
                    </div>
                  )}

                <button type="button" className="btn-create-ai-white" onClick={handleCreateWithAi}>
                  <CreateWithAiSVG />
                  <span>{'Create with AI'}</span>
                </button>
              </>
            )}

            {/* Status 2: CANCELLED */}
            {currentStatus === 'cancelled' && (
              <>
                <div className="card-top-row">
                  <div className="title-with-badge">
                    <h2>{'Pro Plan'}</h2>
                    <span className="status-pill cancelled-pill">{'Cancelled'}</span>
                  </div>
                </div>
                <p className="status-card-description">
                  {`Your Pro subscription has been cancelled. You'll keep access to Ai and motion tracking until ${dynamicAccessEndsDate}.`}
                </p>

                <div className="sub-details-list">
                  <div className="sub-detail-item">
                    <span className="detail-name">{'Access ends'}</span>
                    <span className="detail-value">{dynamicAccessEndsDate}</span>
                  </div>
                  <div className="sub-detail-item">
                    <span className="detail-name">{'After that'}</span>
                    <span className="detail-value">{'Basic Plan'}</span>
                  </div>
                  <div className="sub-detail-item">
                    <span className="detail-name">{'Future charges'}</span>
                    <span className="detail-value">{'None'}</span>
                  </div>
                  <div className="sub-detail-item">
                    <span className="detail-name">{'Payment Method'}</span>
                    <span className="detail-value card-val">
                      {defaultCard ? (
                        <>
                          <CardBrandLogo
                            brand={defaultCard.brand}
                            style={{ width: 32, height: 18 }}
                          />
                          <span>{`${getCardBrandDisplayName(defaultCard.brand)} •••• ${defaultCard.last4}`}</span>
                        </>
                      ) : (
                        <span>{'No payment method'}</span>
                      )}
                    </span>
                  </div>
                </div>

                <button type="button" className="btn-keep-pro-gold" onClick={handleReactivate}>
                  {'Keep My Pro Plan'}
                </button>

                <div className="sub-info-note">
                  <CvvHintSVG />
                  <span>{`Reactivate before ${dynamicAccessEndsDate} to keep your Pro access without interruption.`}</span>
                </div>
              </>
            )}

            {/* Status 3: INACTIVE */}
            {currentStatus === 'inactive' && (
              <>
                <div className="card-top-row">
                  <div className="title-with-badge">
                    <h2>{'Pro Plan'}</h2>
                    <span className="status-pill inactive-pill">{'Inactive'}</span>
                  </div>
                </div>

                <div className="sub-warning-banner">
                  <CardNotChargedShieldSVG />
                  <span>{"Your Pro Plan has expired. You're now on the free Basic Plan."}</span>
                </div>

                <div className="sub-details-list">
                  <div className="sub-detail-item">
                    <span className="detail-name">{'Status'}</span>
                    <span className="detail-value">{'Expired'}</span>
                  </div>
                  <div className="sub-detail-item">
                    <span className="detail-name">{'Expired on'}</span>
                    <span className="detail-value">{dynamicExpiredDate}</span>
                  </div>
                  <div className="sub-detail-item">
                    <span className="detail-name">{'Current Plan'}</span>
                    <span className="detail-value">{'Basic Plan (Free)'}</span>
                  </div>
                  <div className="sub-detail-item">
                    <span className="detail-name">{'Payment Method'}</span>
                    <span className="detail-value card-val">
                      {defaultCard ? (
                        <>
                          <CardBrandLogo
                            brand={defaultCard.brand}
                            style={{ width: 32, height: 18 }}
                          />
                          <span>{`${getCardBrandDisplayName(defaultCard.brand)} •••• ${defaultCard.last4}`}</span>
                        </>
                      ) : (
                        <span>{'No payment method'}</span>
                      )}
                    </span>
                  </div>
                </div>

                {/* Upgrade Callout Banner */}
                <div className="upgrade-callout-banner">
                  <div className="callout-icon-left">
                    <CreateWithAiSVG />
                  </div>
                  <div className="callout-texts">
                    <h4>{'Upgrade to Pro to unlock all features'}</h4>
                    <p>
                      {'Get AI program generation, motion tracking, advanced analytics and more'}
                    </p>
                  </div>
                  <button type="button" className="btn-upgrade-banner" onClick={handleUpgradeToPro}>
                    {'Upgrade to Pro'}
                  </button>
                </div>
              </>
            )}

            {/* Status 4: PAST DUE */}
            {currentStatus === 'past_due' && (
              <>
                <div className="card-top-row">
                  <div className="title-with-badge">
                    <h2>{'Pro Plan'}</h2>
                    <span className="status-pill past-due-pill">{'Past Due'}</span>
                  </div>
                </div>

                <div className="sub-past-due-note">
                  <CardNotChargedShieldSVG />
                  <span>
                    {`We couldn’t process your last payment. Your Pro access is still active until `}
                    <strong>{dynamicGracePeriodDate}</strong>
                    {` while we retry your renewal.`}
                  </span>
                </div>

                <div className="sub-details-list">
                  <div className="sub-detail-item">
                    <span className="detail-name">{'Billing cycle'}</span>
                    <span className="detail-value capitalize">
                      {serverState?.billing_interval || billingCycle}
                    </span>
                  </div>
                  <div className="sub-detail-item">
                    <span className="detail-name">{'Last payment attempt'}</span>
                    <span className="detail-value">{dynamicNextBillingDate}</span>
                  </div>
                  <div className="sub-detail-item">
                    <span className="detail-name">{'Amount due'}</span>
                    <span className="detail-value">
                      {serverState?.past_due?.amount_due || serverState?.amount?.formatted || '—'}
                    </span>
                  </div>
                  <div className="sub-detail-item">
                    <span className="detail-name">{'Grace period ends'}</span>
                    <span className="detail-value highlight-orange">{dynamicGracePeriodDate}</span>
                  </div>
                  <div className="sub-detail-item">
                    <span className="detail-name">{'Payment Method'}</span>
                    <span className="detail-value card-val">
                      {defaultCard ? (
                        <>
                          <CardBrandLogo
                            brand={defaultCard.brand}
                            style={{ width: 32, height: 18 }}
                          />
                          <span>{`${getCardBrandDisplayName(defaultCard.brand)} •••• ${defaultCard.last4}`}</span>
                        </>
                      ) : (
                        <span>{'No payment method'}</span>
                      )}
                    </span>
                  </div>
                </div>

                <div className="past-due-action-buttons">
                  <button
                    type="button"
                    className="btn-update-method-white"
                    onClick={() => setIsPaymentModalOpen(true)}
                  >
                    {'Update Payment Method'}
                  </button>
                  <button type="button" className="btn-retry-dark" onClick={handleRetryPayment}>
                    {'Retry Payment'}
                  </button>
                </div>

                <div className="sub-info-note">
                  <CardNotChargedShieldSVG />
                  <span>{`If payment isn’t completed by ${dynamicGracePeriodDate}, your account will move to the Basic Plan.`}</span>
                </div>
              </>
            )}
          </div>

          {/* Billing Information Card */}
          <div className="sub-card billing-info-sub-card">
            <h3>{'Billing Information'}</h3>
            {currentStatus === 'inactive' ? (
              <p className="card-muted-text">{'Your current Plan is Free.'}</p>
            ) : (
              <>
                <div className="sub-details-list" style={{ marginBottom: 18 }}>
                  <div className="sub-detail-item">
                    <span className="detail-name">
                      {isBusinessBilling ? 'Company Name' : 'Full Name'}
                    </span>
                    <span className="detail-value">
                      {serverBillingProfile?.fullName ||
                        serverBillingProfile?.companyName ||
                        billingInfo.fullName ||
                        billingInfo.companyName ||
                        '—'}
                    </span>
                  </div>
                  <div className="sub-detail-item">
                    <span className="detail-name">{'Billing Email'}</span>
                    <span className="detail-value">
                      {serverBillingProfile?.billingEmail || billingInfo.billingEmail || '—'}
                    </span>
                  </div>
                </div>
                <button
                  type="button"
                  className="btn-card-action"
                  onClick={() => setIsBillingModalOpen(true)}
                >
                  {'Change Billing Information'}
                </button>
              </>
            )}
          </div>
        </div>

        {/* Right Column */}
        <div className="my-sub-right-col">
          {/* Payment Method Card */}
          <div className="sub-card payment-method-sub-card">
            <div className="sub-card-header-icon">
              <PaymentMethodSVG />
              <h3>{'Payment Method'}</h3>
            </div>

            {defaultCard ? (
              <div className="current-card-item">
                <div className="card-brand-wrap">
                  <CardBrandLogo brand={defaultCard.brand} />
                </div>
                <div className="card-details">
                  <span className="card-type">{getCardBrandDisplayName(defaultCard.brand)}</span>
                  <span className="card-number">•••• •••• {defaultCard.last4}</span>
                </div>
                <span className="default-pill">{'Default'}</span>
              </div>
            ) : (
              <p className="card-muted-text">{'No payment method on file.'}</p>
            )}

            {/* Context message based on status */}
            {currentStatus === 'cancelled' && (
              <div className="card-status-note">
                <CardNotChargedShieldSVG />
                <span>
                  {'This card will not be charged again unless you reactivate your subscription'}
                </span>
              </div>
            )}

            {currentStatus === 'inactive' && (
              <div className="card-status-note">
                <CardNotChargedShieldSVG />
                <span>{'This card is not being charged.'}</span>
              </div>
            )}

            {currentStatus === 'past_due' && (
              <div className="card-status-note warning-note">
                <PaymentFailedVectorSVG />
                <span>{'This card was declined for the latest renewal.'}</span>
              </div>
            )}

            {/* Action button */}
            {currentStatus === 'inactive' ? (
              <button
                type="button"
                className="btn-card-action"
                onClick={() => setIsRemoveModalOpen(true)}
              >
                {'Remove Card'}
              </button>
            ) : (
              <button type="button" className="btn-card-action" onClick={handleGoToManagePayment}>
                {currentStatus === 'active' ? 'Change Default' : 'Manage Payment Method'}
              </button>
            )}
          </div>

          {/* Questions? Contact Support Card */}
          <div className="sub-card support-sub-card">
            <div className="sub-card-header-icon">
              <QuestionsContactSupportSVG />
              <div className="sub-card-header-text">
                <h3>{'Questions? Contact support'}</h3>
                <p className="card-muted-text">{"We're here to help you."}</p>
              </div>
            </div>
            <button type="button" className="btn-card-action" onClick={handleGoToSupport}>
              {'Contact Support'}
            </button>
          </div>

          {/* Downgrade Card (Active or Trialing status) */}
          {(currentStatus === 'active' || currentStatus === 'trialing') && (
            <div className="sub-card downgrade-sub-card">
              <div className="sub-card-header-icon">
                <TrendDownSVG />
                <h3>{'Downgrade to Basic'}</h3>
              </div>
              <p className="card-muted-text">
                {"You'll lose your Pro subscription, including AI and motion-tracking features."}
              </p>
              <button
                type="button"
                className="btn-card-action btn-danger-action"
                onClick={handleCancelSubscription}
              >
                {'Give up Pro features'}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Modals */}
      <BillingInfoModal
        isOpen={isBillingModalOpen}
        onClose={() => setIsBillingModalOpen(false)}
        onSuccess={() => {
          void refetchBillingProfile();
          void refetchState();
        }}
      />

      <PaymentMethodModal
        isOpen={isPaymentModalOpen}
        onClose={() => setIsPaymentModalOpen(false)}
        onSuccess={() => {
          void refetchPaymentMethods();
          void refetchState();
        }}
      />

      <RemovePaymentMethodModal
        isOpen={isRemoveModalOpen}
        onClose={() => setIsRemoveModalOpen(false)}
        onConfirmRemove={handleConfirmRemoveCard}
      />
    </div>
  );
};
